/**
 * One-click "Add to calendar" links for a meeting — the same set the invitation email carries
 * (EnterpriseConnect/bff/Meetings/MeetingInvite.cs BuildCalendarLinks). The .ics link is the anonymous
 * PUBLISH file (`/api/meetings/{id}/calendar.ics`), which Outlook (any account type), Apple Calendar and
 * other apps import; it shares the invitation's UID so it updates rather than duplicates an entry.
 */
export interface CalendarLinkInput {
    id: string;
    subject: string;
    /** ISO instants. */
    start: string;
    end?: string | null;
    description?: string | null;
    joinUrl: string;
    /** Absolute base of the Connect BFF API (the web app's own origin in production). */
    apiBase: string;
}

export interface CalendarLink {
    id: "ics" | "google" | "office365" | "outlook" | "yahoo";
    label: string;
    href: string;
}

const DEFAULT_MINUTES = 60;

function stamp(d: Date): string {
    return d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
}

function iso(d: Date): string {
    return d.toISOString().replace(/\.\d{3}Z$/, "Z");
}

export function calendarLinks(input: CalendarLinkInput): CalendarLink[] {
    const start = new Date(input.start);
    const end = input.end ? new Date(input.end) : new Date(start.getTime() + DEFAULT_MINUTES * 60_000);
    const title = encodeURIComponent(input.subject);
    const body = encodeURIComponent(
        input.description?.trim() ? `${input.description.trim()}\n\nJoin the meeting: ${input.joinUrl}` : `Join the meeting: ${input.joinUrl}`
    );
    const location = encodeURIComponent(input.joinUrl);
    const outlook = (host: string) =>
        `https://${host}/calendar/0/deeplink/compose?path=%2Fcalendar%2Faction%2Fcompose&rru=addevent` +
        `&subject=${title}&startdt=${iso(start)}&enddt=${iso(end)}&body=${body}&location=${location}`;
    return [
        {
            id: "ics",
            label: "Outlook / Apple (.ics)",
            href: new URL(`/api/meetings/${encodeURIComponent(input.id)}/calendar.ics`, input.apiBase).toString()
        },
        {
            id: "google",
            label: "Google Calendar",
            href: `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${stamp(start)}/${stamp(end)}&details=${body}&location=${location}`
        },
        { id: "office365", label: "Microsoft 365", href: outlook("outlook.office.com") },
        { id: "outlook", label: "Outlook.com", href: outlook("outlook.live.com") },
        {
            id: "yahoo",
            label: "Yahoo Calendar",
            href: `https://calendar.yahoo.com/?v=60&title=${title}&st=${stamp(start)}&et=${stamp(end)}&desc=${body}&in_loc=${location}`
        }
    ];
}
